import { useState, useEffect, useRef, useCallback, ChangeEvent } from 'react'
import { createPortal } from 'react-dom'
import type { Book } from '@/types'
import { supabase } from '../../lib/supabase'
import { searchBookInfo } from '../../utils/googleBooks'
import { searchOpenLibraryInfo } from '../../utils/openLibrary'
import { FIELD_LIMITS, truncateField, validateAuthors, validateCategories, normalizeToArray } from '../../utils/validation'
import { useModalBehavior } from '../../hooks/useModalBehavior'
import CustomConfirmModal from '../common/CustomConfirmModal'
import TagInput from '../common/TagInput'
import styles from './BookDetailModal.module.css'
import {
    FORMAT_OPTIONS,
    STATUS_OPTIONS,
    PROGRESS_OPTIONS,
    getFormatLabel,
    getStatusLabel,
    getProgressLabel,
} from '@/constants/bookOptions'



interface BookDetailModalProps {
    book: Book | Partial<Book>
    onClose: () => void
    onSave?: (book: Book | Partial<Book>) => void
    existingAuthors?: string[]
    existingPublishers?: string[]
    existingLocations?: string[]
    existingSeries?: string[]
    existingCategories?: string[]
    existingLanguages?: string[]
    readOnly?: boolean
    ownerName?: string | null
    visibleFields?: Record<string, boolean> | null
}

interface ReadingList {
    id: string
    name: string
}

interface FormData {
    title: string
    author: string[]
    notes: string
    synopsis: string
    description: string
    page_count: string | number
    format: string
    status: string
    progress: string
    publisher: string
    location: string
    series: string
    series_number: string | number
    category: string[]
    language: string
}

interface NotificationState {
    isOpen: boolean
    title: string
    message: string
    type: 'success' | 'danger' | 'warning' | 'info'
}

// Helper functions for labels

export default function BookDetailModal({
    book,
    onClose,
    onSave,
    existingPublishers = [],
    existingLocations = [],
    existingSeries = [],
    existingCategories = [],
    existingLanguages = [],
    readOnly = false,
    ownerName = null,
    visibleFields = null,
}: Readonly<BookDetailModalProps>) {
    const [formData, setFormData] = useState<FormData>({
        title: book?.title || '',
        author: normalizeToArray(book?.author),
        notes: book?.notes || '',
        synopsis: book?.synopsis || '',
        description: book?.description || '',
        page_count: book?.page_count ?? '',
        format: book?.format || 'physical',
        status: book?.status || 'owned',
        progress: book?.progress || '',
        publisher: book?.publisher || '',
        location: book?.location || '',
        series: book?.series || '',
        series_number: book?.series_number ?? '',
        category: normalizeToArray(book?.category),
        language: book?.language || '',
    })

    const [saving, setSaving] = useState(false)
    const [showPublisherSuggestions, setShowPublisherSuggestions] = useState(false)
    const [showLocationSuggestions, setShowLocationSuggestions] = useState(false)
    const [showSeriesSuggestions, setShowSeriesSuggestions] = useState(false)
    const [showLanguageSuggestions, setShowLanguageSuggestions] = useState(false)

    const [bookLists, setBookLists] = useState<ReadingList[]>([])
    const [allLists, setAllLists] = useState<ReadingList[]>([])
    const [newListName, setNewListName] = useState('')
    const [showListInput, setShowListInput] = useState(false)
    const [addingToList, setAddingToList] = useState(false)
    const [fetchingInfo, setFetchingInfo] = useState(false)

    const [notification, setNotification] = useState<NotificationState>({
        isOpen: false,
        title: '',
        message: '',
        type: 'info',
    })

    const showNotify = (title: string, message: string, type: NotificationState['type'] = 'info') => {
        setNotification({ isOpen: true, title, message, type })
    }

    const isVisible = (field: string): boolean => {
        if (!readOnly || !visibleFields) return true
        return Object.hasOwn(visibleFields, field) && visibleFields[field] === true
    }

    const modalRef = useRef<HTMLDivElement>(null)
    const publisherInputRef = useRef<HTMLDivElement>(null)
    const locationInputRef = useRef<HTMLDivElement>(null)
    const seriesInputRef = useRef<HTMLDivElement>(null)
    const languageInputRef = useRef<HTMLDivElement>(null)
    const listInputRef = useRef<HTMLDivElement>(null)

    useModalBehavior({
        modalRef,
        onClose,
        suggestionRefs: [
            { ref: publisherInputRef, setter: setShowPublisherSuggestions },
            { ref: locationInputRef, setter: setShowLocationSuggestions },
            { ref: seriesInputRef, setter: setShowSeriesSuggestions },
            { ref: languageInputRef, setter: setShowLanguageSuggestions },
            { ref: listInputRef, setter: setShowListInput },
        ],
    })

    const fetchListsInfo = useCallback(async () => {
        try {
            const {
                data: { user },
            } = await supabase.auth.getUser()
            if (!user) return

            const { data: userLists } = await supabase.from('reading_lists').select('id, name').eq('user_id', user.id).order('name')

            if (userLists) setAllLists(userLists as ReadingList[])

            const { data: bookInLists } = await supabase.from('reading_list_items').select('list_id').eq('book_id', book.id)

            if (bookInLists && userLists) {
                const includedIds = bookInLists.map((i: { list_id: string }) => i.list_id)
                setBookLists((userLists as ReadingList[]).filter((l) => includedIds.includes(l.id)))
            }
        } catch (error) {
            console.error('Error fetching lists info:', error)
        }
    }, [book?.id])

    useEffect(() => {
        if (book?.id && !readOnly) {
            fetchListsInfo()
        }
    }, [book?.id, readOnly, fetchListsInfo])

    const handleAddToExistingList = async (list: ReadingList) => {
        try {
            const { error } = await supabase.from('reading_list_items').insert([{ list_id: list.id, book_id: book.id }])

            if (error) throw error

            setBookLists([...bookLists, list])
            setShowListInput(false)
            setNewListName('')
        } catch (error) {
            console.error('Error adding to list:', error)
        }
    }

    const handleCreateAndAddList = async () => {
        if (!newListName.trim()) return
        setAddingToList(true)
        try {
            const {
                data: { user },
            } = await supabase.auth.getUser()
            if (!user) throw new Error('No user found')

            const { data: newListData, error: createError } = await supabase
                .from('reading_lists')
                .insert([{ name: truncateField(newListName.trim(), FIELD_LIMITS.readingLists.name), user_id: user.id }])
                .select()
                .single()

            if (createError) throw createError
            const newList = newListData as ReadingList

            const { error: addError } = await supabase.from('reading_list_items').insert([{ list_id: newList.id, book_id: book.id }])

            if (addError) throw addError

            setAllLists([...allLists, newList])
            setBookLists([...bookLists, newList])
            setShowListInput(false)
            setNewListName('')
        } catch (error) {
            console.error('Error creating list:', error)
            showNotify('Error', 'Error al crear la lista', 'danger')
        } finally {
            setAddingToList(false)
        }
    }

    const handleRemoveFromList = async (listId: string) => {
        try {
            const { error } = await supabase.from('reading_list_items').delete().match({ list_id: listId, book_id: book.id })

            if (error) throw error

            setBookLists(bookLists.filter((l) => l.id !== listId))
        } catch (error) {
            console.error('Error removing from list:', error)
        }
    }

    const handleChange = (field: keyof FormData, value: string | string[]) => {
        if (readOnly) return
        setFormData((prev) => ({ ...prev, [field]: value }))
    }

    const handleSeriesNumberChange = (value: string) => {
        if (readOnly) return
        if (value === '' || /^\d+$/.test(value)) {
            setFormData((prev) => ({ ...prev, series_number: value }))
        }
    }

    const handlePageCountChange = (value: string) => {
        if (readOnly) return
        if (value === '' || /^\d+$/.test(value)) {
            setFormData((prev) => ({ ...prev, page_count: value }))
        }
    }

    const handleFetchInfo = async () => {
        if (!formData.title) return
        setFetchingInfo(true)
        try {
            const [googleInfo, olInfo] = await Promise.all([
                searchBookInfo(formData.title, formData.author).catch(() => null),
                searchOpenLibraryInfo(formData.title, formData.author).catch(() => null),
            ])

            if (googleInfo || olInfo) {
                const newAuthors = googleInfo?.authors || olInfo?.authors || []
                const mergedAuthors = [...formData.author]
                newAuthors.forEach((author: string) => {
                    if (!mergedAuthors.some((a) => a.toLowerCase() === author.toLowerCase())) {
                        mergedAuthors.push(author)
                    }
                })

                const newCategories = googleInfo?.categories || olInfo?.categories || []
                const mergedCategories = [...formData.category]
                newCategories.forEach((cat: string) => {
                    if (!mergedCategories.some((c) => c.toLowerCase() === cat.toLowerCase())) {
                        mergedCategories.push(cat)
                    }
                })

                setFormData((prev) => {
                    const b = FIELD_LIMITS.books
                    let page_count = prev.page_count
                    if (googleInfo?.page_count) {
                        page_count = String(googleInfo.page_count)
                    } else if (olInfo?.page_count) {
                        page_count = String(olInfo.page_count)
                    }

                    return {
                        ...prev,
                        description: truncateField(googleInfo?.description || olInfo?.description || prev.description, b.description),
                        author: validateAuthors(mergedAuthors),
                        category: validateCategories(mergedCategories),
                        page_count: page_count,
                        publisher: truncateField(googleInfo?.publisher || olInfo?.publisher || prev.publisher, b.publisher),
                    }
                })
            } else {
                console.log('No info found in Google Books or Open Library')
            }
        } catch (error) {
            console.error('Error fetching info:', error)
        } finally {
            setFetchingInfo(false)
        }
    }

    const handleSave = async () => {
        if (readOnly) {
            onClose()
            return
        }

        setSaving(true)

        const bookLimits = FIELD_LIMITS.books
        const truncatedData = {
            title: formData.title,
            author: validateAuthors(formData.author),
            notes: truncateField(formData.notes, bookLimits.notes),
            synopsis: truncateField(formData.synopsis, bookLimits.synopsis) || null,
            description: truncateField(formData.description, bookLimits.description) || null,
            page_count: formData.page_count === '' ? null : parseInt(String(formData.page_count), 10),
            format: formData.format,
            status: formData.status,
            progress: formData.progress,
            publisher: truncateField(formData.publisher, bookLimits.publisher) || null,
            location: truncateField(formData.location, bookLimits.location) || null,
            series: truncateField(formData.series, bookLimits.series) || null,
            series_number: formData.series_number === '' ? null : parseInt(String(formData.series_number), 10),
            category: validateCategories(formData.category),
            language: truncateField(formData.language, bookLimits.language) || null,
        }

        try {
            await onSave?.(book.id ? ({ ...book, ...truncatedData } as Book) : truncatedData)
            // onClose() removed: allow parent to control closing on success (setSelectedBook(null))
        } catch (error) {
            console.error('Error saving book:', error)
            showNotify('Error', 'No se pudo guardar el libro', 'danger')
        } finally {
            setSaving(false)
        }
    }

    const filteredPublishers = existingPublishers.filter((e) => e && e.toLowerCase().includes(formData.publisher.toLowerCase()) && e !== formData.publisher)
    const filteredLocations = existingLocations.filter((u) => u && u.toLowerCase().includes(formData.location.toLowerCase()) && u !== formData.location)
    const filteredSeries = existingSeries.filter((s) => s && s.toLowerCase().includes(formData.series.toLowerCase()) && s !== formData.series)
    const filteredLanguages = existingLanguages.filter((i) => i && i.toLowerCase().includes(formData.language.toLowerCase()) && i !== formData.language)

    return createPortal(
        <>
            <div className={styles.bookDetailOverlay}>
                <div className={`${styles.bookDetailModal} ${readOnly ? styles.readOnly : ''}`} ref={modalRef}>
                    {readOnly && ownerName && (
                        <div
                            style={{
                                backgroundColor: '#f0f4ff',
                                borderBottom: '1px solid #dbeafe',
                                padding: '8px 16px',
                                textAlign: 'center',
                                fontSize: '0.9rem',
                                color: '#1e40af',
                                fontWeight: '500',
                            }}
                        >
                            👩‍🚀 Viendo libro compartido de {ownerName}
                        </div>
                    )}
                    <div className={styles.bookDetailHeader}>
                        <div className={styles.bookDetailTitleSection}>
                            <span className={styles.bookDetailIcon}>📖</span>
                            <div className={styles.bookDetailTitleInfo}>
                                <input
                                    type="text"
                                    data-testid="book-title-input"
                                    className={styles.bookDetailTitleInput}
                                    value={formData.title}
                                    onChange={(e: ChangeEvent<HTMLInputElement>) => handleChange('title', e.target.value)}
                                    placeholder="Título del libro"
                                    disabled={readOnly}
                                />
                                <div className={styles.authorInputWrapper}>
                                    <span className={styles.authorIcon} title="Campo frecuentemente editado">
                                        ✍️
                                    </span>
                                    <TagInput
                                        value={formData.author}
                                        onChange={(newAuthors) => handleChange('author', newAuthors)}
                                        suggestions={[]}
                                        placeholder="Añadir autor..."
                                        maxTags={FIELD_LIMITS.books.author_max_items}
                                        maxLength={FIELD_LIMITS.books.author_max_length}
                                        disabled={readOnly}
                                        dataTestId="author-input"
                                    />
                                </div>
                            </div>
                        </div>
                        <div className={styles.bookDetailActions}>
                            {!readOnly && (
                                <button className={`btn-secondary ${styles.fetchInfoBtnModal}`} onClick={handleFetchInfo} disabled={fetchingInfo || !formData.title} title="Buscar información en Google Books">
                                    {fetchingInfo ? '🔍...' : '🌐 Buscar Info'}
                                </button>
                            )}
                            <button className={styles.bookDetailClose} onClick={onClose} aria-label="Cerrar">
                                ✕
                            </button>
                        </div>
                    </div>

                    <div className={styles.bookDetailBody}>
                        {isVisible('synopsis') && (
                            <div className={styles.formGroup}>
                                <label className={styles.formLabel} htmlFor="synopsis">
                                    📄 Sinopsis
                                </label>
                                <textarea
                                    id="synopsis"
                                    data-testid="synopsis-input"
                                    className={styles.formTextarea}
                                    value={formData.synopsis}
                                    onChange={(e: ChangeEvent<HTMLTextAreaElement>) => handleChange('synopsis', truncateField(e.target.value, FIELD_LIMITS.books.synopsis))}
                                    placeholder="Breve resumen del libro..."
                                    rows={3}
                                    maxLength={FIELD_LIMITS.books.synopsis}
                                    disabled={readOnly}
                                />
                                {!readOnly && (
                                    <span className={`${styles.charCounter} ${formData.synopsis.length >= FIELD_LIMITS.books.synopsis * 0.9 ? styles.warning : ''}`}>
                                        {formData.synopsis.length}/{FIELD_LIMITS.books.synopsis}
                                    </span>
                                )}
                            </div>
                        )}

                        {isVisible('description') && (
                            <div className={styles.formGroup}>
                                <label className={styles.formLabel} htmlFor="description">
                                    📋 Descripción
                                </label>
                                <textarea
                                    id="description"
                                    className={styles.formTextarea}
                                    value={formData.description}
                                    onChange={(e: ChangeEvent<HTMLTextAreaElement>) => handleChange('description', truncateField(e.target.value, FIELD_LIMITS.books.description))}
                                    placeholder="Descripción adicional del libro..."
                                    rows={3}
                                    maxLength={FIELD_LIMITS.books.description}
                                    disabled={readOnly}
                                />
                                {!readOnly && (
                                    <span className={`${styles.charCounter} ${formData.description.length >= FIELD_LIMITS.books.description * 0.9 ? styles.warning : ''}`}>
                                        {formData.description.length}/{FIELD_LIMITS.books.description}
                                    </span>
                                )}
                            </div>
                        )}

                        {isVisible('page_count') && (
                            <div className={styles.formGroup}>
                                <label className={styles.formLabel} htmlFor="page_count">
                                    📖 Número de Páginas
                                </label>
                                <input
                                    type="text"
                                    inputMode="numeric"
                                    id="page_count"
                                    data-testid="page-count-input"
                                    className={styles.formInput}
                                    value={formData.page_count}
                                    onChange={(e: ChangeEvent<HTMLInputElement>) => handlePageCountChange(e.target.value)}
                                    placeholder="Ej: 350"
                                    autoComplete="off"
                                    disabled={readOnly}
                                />
                            </div>
                        )}

                        {isVisible('notes') && (
                            <div className={styles.formGroup}>
                                <label className={styles.formLabel} htmlFor="notes">
                                    📝 Notas
                                </label>
                                <textarea
                                    id="notes"
                                    className={styles.formTextarea}
                                    value={formData.notes}
                                    onChange={(e: ChangeEvent<HTMLTextAreaElement>) => handleChange('notes', truncateField(e.target.value, FIELD_LIMITS.books.notes))}
                                    placeholder="Añade notas sobre este libro..."
                                    rows={3}
                                    maxLength={FIELD_LIMITS.books.notes}
                                    disabled={readOnly}
                                />
                                {!readOnly && (
                                    <span className={`${styles.charCounter} ${formData.notes.length >= FIELD_LIMITS.books.notes * 0.9 ? styles.warning : ''}`}>
                                        {formData.notes.length}/{FIELD_LIMITS.books.notes}
                                    </span>
                                )}
                            </div>
                        )}

                        <div className={styles.formRow}>
                            {isVisible('format') && (
                                <div className={styles.formGroup}>
                                    <label className={styles.formLabel} htmlFor="format">
                                        📦 Formato
                                    </label>
                                    <select
                                        id="format"
                                        className={styles.formSelect}
                                        value={formData.format}
                                        onChange={(e: ChangeEvent<HTMLSelectElement>) => handleChange('format', e.target.value)}
                                        disabled={readOnly}
                                    >
                                        {FORMAT_OPTIONS.map((opt) => (
                                            <option key={opt} value={opt}>
                                                {getFormatLabel(opt)}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            )}

                            {isVisible('status') && (
                                <div className={styles.formGroup}>
                                    <label className={styles.formLabel} htmlFor="status">
                                        🏷️ Estado
                                    </label>
                                    <select
                                        id="status"
                                        className={styles.formSelect}
                                        value={formData.status}
                                        onChange={(e: ChangeEvent<HTMLSelectElement>) => handleChange('status', e.target.value)}
                                        disabled={readOnly}
                                    >
                                        {STATUS_OPTIONS.map((opt) => (
                                            <option key={opt} value={opt}>
                                                {getStatusLabel(opt)}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            )}

                            {isVisible('progress') && (
                                <div className={styles.formGroup}>
                                    <label className={styles.formLabel} htmlFor="progress">
                                        📊 Progreso
                                    </label>
                                    <select
                                        id="progress"
                                        className={styles.formSelect}
                                        value={formData.progress}
                                        onChange={(e: ChangeEvent<HTMLSelectElement>) => handleChange('progress', e.target.value)}
                                        disabled={readOnly}
                                    >
                                        {['', ...PROGRESS_OPTIONS].map((opt, idx) => (
                                            <option key={idx} value={opt}>
                                                {getProgressLabel(opt)}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            )}
                        </div>

                        <div className={styles.formRow}>
                            {isVisible('category') && (
                                <div className={styles.formGroup}>
                                    <label className={styles.formLabel} htmlFor="category">
                                        🏷️ Categoría
                                    </label>
                                    <TagInput
                                        value={formData.category}
                                        onChange={(newCategories) => handleChange('category', newCategories)}
                                        suggestions={existingCategories}
                                        placeholder="Añadir categoría..."
                                        maxTags={FIELD_LIMITS.books.category_max_items}
                                        maxLength={FIELD_LIMITS.books.category}
                                        disabled={readOnly}
                                    />
                                </div>
                            )}

                            {isVisible('language') && (
                                <div className={`${styles.formGroup} ${styles.formGroupHybrid}`} ref={languageInputRef}>
                                    <label className={styles.formLabel} htmlFor="language">
                                        🌐 Idioma
                                    </label>
                                    <div className={styles.hybridInputWrapper}>
                                        <input
                                            type="text"
                                            id="language"
                                            className={styles.formInput}
                                            value={formData.language}
                                            onChange={(e: ChangeEvent<HTMLInputElement>) => handleChange('language', truncateField(e.target.value, FIELD_LIMITS.books.language))}
                                            onFocus={() => !readOnly && setShowLanguageSuggestions(true)}
                                            placeholder="Idioma..."
                                            maxLength={FIELD_LIMITS.books.language}
                                            autoComplete="off"
                                            disabled={readOnly}
                                        />
                                        {!readOnly && showLanguageSuggestions && filteredLanguages.length > 0 && (
                                            <div className={styles.suggestionsDropdown}>
                                                {filteredLanguages.slice(0, 5).map((suggestion, idx) => (
                                                    <button
                                                        key={idx}
                                                        className={styles.suggestionItem}
                                                        onClick={() => {
                                                            handleChange('language', suggestion)
                                                            setShowLanguageSuggestions(false)
                                                        }}
                                                    >
                                                        {suggestion}
                                                    </button>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                    {!readOnly && (
                                        <span className={`${styles.charCounter} ${formData.language.length >= FIELD_LIMITS.books.language * 0.9 ? styles.warning : ''}`}>
                                            {formData.language.length}/{FIELD_LIMITS.books.language}
                                        </span>
                                    )}
                                </div>
                            )}
                        </div>

                        <div className={styles.formRow}>
                            {isVisible('publisher') && (
                                <div className={`${styles.formGroup} ${styles.formGroupHybrid}`} ref={publisherInputRef}>
                                    <label className={styles.formLabel} htmlFor="publisher">
                                        🏢 Editorial
                                    </label>
                                    <div className={styles.hybridInputWrapper}>
                                        <input
                                            type="text"
                                            id="publisher"
                                            data-testid="publisher-input"
                                            className={styles.formInput}
                                            value={formData.publisher}
                                            onChange={(e: ChangeEvent<HTMLInputElement>) => handleChange('publisher', truncateField(e.target.value, FIELD_LIMITS.books.publisher))}
                                            onFocus={() => !readOnly && setShowPublisherSuggestions(true)}
                                            placeholder="Nombre de la editorial..."
                                            maxLength={FIELD_LIMITS.books.publisher}
                                            autoComplete="off"
                                            disabled={readOnly}
                                        />
                                        {!readOnly && showPublisherSuggestions && filteredPublishers.length > 0 && (
                                            <div className={styles.suggestionsDropdown}>
                                                {filteredPublishers.slice(0, 5).map((suggestion, idx) => (
                                                    <button
                                                        key={idx}
                                                        className={styles.suggestionItem}
                                                        onClick={() => {
                                                            handleChange('publisher', suggestion)
                                                            setShowPublisherSuggestions(false)
                                                        }}
                                                    >
                                                        {suggestion}
                                                    </button>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                    {!readOnly && (
                                        <span className={`${styles.charCounter} ${formData.publisher.length >= FIELD_LIMITS.books.publisher * 0.9 ? styles.warning : ''}`}>
                                            {formData.publisher.length}/{FIELD_LIMITS.books.publisher}
                                        </span>
                                    )}
                                </div>
                            )}

                            {isVisible('location') && (
                                <div className={`${styles.formGroup} ${styles.formGroupHybrid}`} ref={locationInputRef}>
                                    <label className={styles.formLabel} htmlFor="location">
                                        📍 Ubicación
                                    </label>
                                    <div className={styles.hybridInputWrapper}>
                                        <input
                                            type="text"
                                            id="location"
                                            className={styles.formInput}
                                            value={formData.location}
                                            onChange={(e: ChangeEvent<HTMLInputElement>) => handleChange('location', truncateField(e.target.value, FIELD_LIMITS.books.location))}
                                            onFocus={() => !readOnly && setShowLocationSuggestions(true)}
                                            placeholder="Dónde está el libro..."
                                            maxLength={FIELD_LIMITS.books.location}
                                            autoComplete="off"
                                            disabled={readOnly}
                                        />
                                        {!readOnly && showLocationSuggestions && filteredLocations.length > 0 && (
                                            <div className={styles.suggestionsDropdown}>
                                                {filteredLocations.slice(0, 5).map((suggestion, idx) => (
                                                    <button
                                                        key={idx}
                                                        className={styles.suggestionItem}
                                                        onClick={() => {
                                                            handleChange('location', suggestion)
                                                            setShowLocationSuggestions(false)
                                                        }}
                                                    >
                                                        {suggestion}
                                                    </button>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                    {!readOnly && (
                                        <span className={`${styles.charCounter} ${formData.location.length >= FIELD_LIMITS.books.location * 0.9 ? styles.warning : ''}`}>
                                            {formData.location.length}/{FIELD_LIMITS.books.location}
                                        </span>
                                    )}
                                </div>
                            )}
                        </div>

                        {isVisible('series') && (
                            <div className={styles.formRow}>
                                <div className={`${styles.formGroup} ${styles.formGroupHybrid} ${styles.formGroupWide}`} ref={seriesInputRef}>
                                    <label className={styles.formLabel} htmlFor="series">
                                        📚 Serie
                                    </label>
                                    <div className={styles.hybridInputWrapper}>
                                        <input
                                            type="text"
                                            id="series"
                                            className={styles.formInput}
                                            value={formData.series}
                                            onChange={(e: ChangeEvent<HTMLInputElement>) => handleChange('series', truncateField(e.target.value, FIELD_LIMITS.books.series))}
                                            onFocus={() => !readOnly && setShowSeriesSuggestions(true)}
                                            placeholder="Nombre de la series..."
                                            maxLength={FIELD_LIMITS.books.series}
                                            autoComplete="off"
                                            disabled={readOnly}
                                        />
                                        {!readOnly && showSeriesSuggestions && filteredSeries.length > 0 && (
                                            <div className={styles.suggestionsDropdown}>
                                                {filteredSeries.slice(0, 5).map((suggestion, idx) => (
                                                    <button
                                                        key={idx}
                                                        className={styles.suggestionItem}
                                                        onClick={() => {
                                                            handleChange('series', suggestion)
                                                            setShowSeriesSuggestions(false)
                                                        }}
                                                    >
                                                        {suggestion}
                                                    </button>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                    {!readOnly && (
                                        <span className={`${styles.charCounter} ${formData.series.length >= FIELD_LIMITS.books.series * 0.9 ? styles.warning : ''}`}>
                                            {formData.series.length}/{FIELD_LIMITS.books.series}
                                        </span>
                                    )}
                                </div>

                                <div className={`${styles.formGroup} ${styles.formGroupNarrow}`}>
                                    <label className={styles.formLabel} htmlFor="series_number">
                                        #️⃣ Nº de Serie
                                    </label>
                                    <input
                                        type="text"
                                        inputMode="numeric"
                                        id="series_number"
                                        className={styles.formInput}
                                        value={formData.series_number}
                                        onChange={(e: ChangeEvent<HTMLInputElement>) => handleSeriesNumberChange(e.target.value)}
                                        placeholder="Ej: 1"
                                        autoComplete="off"
                                        disabled={readOnly}
                                    />
                                </div>
                            </div>
                        )}

                        {!readOnly && book?.id && (
                            <div className={`${styles.formGroup} lists-section`}>
                                <label className={styles.formLabel}>📑 Listas de lectura</label>
                                <div className={styles.listsContainerModal}>
                                    <div className={styles.activeListsChips}>
                                        {bookLists.map((list) => (
                                            <div key={list.id} className={styles.listChip}>
                                                <span className="list-chip-name">{list.name}</span>
                                                <button className={styles.listChipRemove} onClick={() => handleRemoveFromList(list.id)} title="Quitar de la lista">
                                                    ×
                                                </button>
                                            </div>
                                        ))}
                                        {bookLists.length === 0 && <span className={styles.noListsText}>No está en ninguna lista</span>}
                                    </div>

                                    <div className={styles.addListWrapper} ref={listInputRef}>
                                        <button className={styles.addListBtnTrigger} onClick={() => setShowListInput(!showListInput)}>
                                            + Añadir a lista
                                        </button>

                                        {showListInput && (
                                            <div className={styles.listInputDropdown}>
                                                <input
                                                    type="text"
                                                    className={styles.listDropdownInput}
                                                    placeholder="Buscar o crear lista..."
                                                    value={newListName}
                                                    onChange={(e: ChangeEvent<HTMLInputElement>) => setNewListName(e.target.value)}
                                                    autoFocus
                                                />
                                                <div className={styles.listSuggestions}>
                                                    {allLists
                                                        .filter((l) => !bookLists.find((bl) => bl.id === l.id))
                                                        .filter((l) => l.name.toLowerCase().includes(newListName.toLowerCase()))
                                                        .slice(0, 5)
                                                        .map((list) => (
                                                            <button key={list.id} className={styles.listSuggestionItem} onClick={() => handleAddToExistingList(list)}>
                                                                📂 {list.name}
                                                            </button>
                                                        ))}
                                                    {newListName && !allLists.find((l) => l.name.toLowerCase() === newListName.toLowerCase()) && (
                                                        <button className={`${styles.listSuggestionItem} ${styles.createNew}`} onClick={handleCreateAndAddList} disabled={addingToList}>
                                                            {addingToList ? '⏳ Creando...' : `＋ Crear "${newListName}"`}
                                                        </button>
                                                    )}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    <div className={styles.bookDetailFooter}>
                        <button className="btn-secondary" onClick={onClose} data-testid="cancel-book-button">
                            {readOnly ? 'Cerrar' : 'Cancelar'}
                        </button>
                        {!readOnly && (
                            <button className="btn-primary" onClick={handleSave} disabled={saving || (!book?.id && !formData.title.trim())} data-testid="save-book-button">
                                {saving ? (
                                    <>
                                        <span className={`${styles.spinner} ${styles.small}`}></span>
                                        Guardando...
                                    </>
                                ) : (
                                    <>💾 Guardar</>
                                )}
                            </button>
                        )}
                    </div>
                </div>
            </div>

            <CustomConfirmModal
                isOpen={notification.isOpen}
                type={notification.type}
                title={notification.title}
                message={notification.message}
                showCancel={false}
                confirmText="Entendido"
                onConfirm={() => setNotification({ ...notification, isOpen: false })}
            />
        </>,
        document.body
    )
}
